import { action, makeObservable, observable } from "mobx";

import { canAccessPathWithRole, resolveRoleGateForPath } from "@/routes/roleGateMetadata";

const ACTIVE_ROLE_KEY = "tars_active_role_v1";

function readPersistedRole(availableRoles = [], hasRole = null) {
  if (typeof localStorage === "undefined") return "";
  try {
    const stored = String(localStorage.getItem(ACTIVE_ROLE_KEY) ?? "")
      .trim()
      .toLowerCase();
    if (!stored) return "";
    if (availableRoles.includes(stored)) return stored;
    if (typeof hasRole === "function" && hasRole(stored)) return stored;
    return "";
  } catch {
    return "";
  }
}

class RoleReconciliationStore {
  isDialogOpen = false;
  currentPathname = "";
  pageRole = "";
  stayRole = "";
  _lastPromptKey = "";
  _lastPathname = "";

  constructor() {
    makeObservable(this, {
      isDialogOpen: observable,
      currentPathname: observable,
      pageRole: observable,
      stayRole: observable,
      openDialog: action,
      closeDialog: action,
      evaluatePathOnFocus: action,
      resolveSelection: action,
      dismissDialog: action,
    });
  }

  _clearDialogState() {
    this.isDialogOpen = false;
    this.currentPathname = "";
    this.pageRole = "";
    this.stayRole = "";
  }

  openDialog({ pathname, pageRole, stayRole }) {
    this.currentPathname = pathname;
    this.pageRole = pageRole;
    this.stayRole = stayRole;
    this.isDialogOpen = true;
  }

  closeDialog() {
    this._clearDialogState();
  }

  /**
   * Evaluates the current route on tab focus/visibility and opens a reconciliation dialog
   * when the active role can no longer access the route but the associated role is available.
   *
   * @param {{ pathname: string, authStore: object }} params
   * @returns {null | { pathname: string, pageRole: string, stayRole: string }}
   */
  evaluatePathOnFocus({ pathname, authStore }) {
    const normalizedPath = String(pathname ?? "").trim();
    if (!normalizedPath || !authStore?.userId) return null;

    if (normalizedPath !== this._lastPathname) {
      this._lastPathname = normalizedPath;
      this._lastPromptKey = "";
      if (!this.isDialogOpen) {
        this.currentPathname = normalizedPath;
      }
    }

    const rule = resolveRoleGateForPath(normalizedPath);
    if (!rule) return null;
    const availableRoles = Array.isArray(authStore?.availableRoles) ? authStore.availableRoles : [];
    const activeRole = String(authStore?.activeRole ?? authStore?.role ?? "")
      .trim()
      .toLowerCase();
    const persistedRole = readPersistedRole(availableRoles, authStore?.hasRole?.bind(authStore));
    const stayRole = persistedRole || activeRole;
    if (!stayRole) return null;
    if (canAccessPathWithRole(normalizedPath, stayRole, authStore)) return null;

    const requiredRole = String(rule.requiredRole ?? "")
      .trim()
      .toLowerCase();
    if (!requiredRole || !authStore?.hasRole?.(requiredRole)) return null;
    if (stayRole === requiredRole) return null;

    const promptKey = `${normalizedPath}|${stayRole}|${requiredRole}`;
    if (promptKey === this._lastPromptKey || this.isDialogOpen) return null;
    this._lastPromptKey = promptKey;

    const payload = {
      pathname: normalizedPath,
      pageRole: requiredRole,
      stayRole,
    };
    this.openDialog(payload);
    return payload;
  }

  /**
   * @param {"stay"|"page"} selection
   * @returns {null | { selectedRole: string, canStayOnPage: boolean }}
   */
  resolveSelection(selection, authStore) {
    const selectedRole = selection === "page" ? this.pageRole : this.stayRole;
    if (!selectedRole) {
      this.closeDialog();
      return null;
    }
    const canStayOnPage = canAccessPathWithRole(this.currentPathname, selectedRole, authStore);
    return { selectedRole, canStayOnPage };
  }

  dismissDialog() {
    this.closeDialog();
  }
}

export default RoleReconciliationStore;
